import React from 'react';
import { TextField, StandardTextFieldProps, InputProps } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { noop } from 'lodash';

// Hide browser's default number input controls
const hideNumberInputControls = {
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};

type NumericInputEvent = {
  target: {
    name: string;
    value: number | string;
  };
};

interface NumericFormatCustomProps {
  allowNegative?: boolean;
  decimalScale?: number;
  fixedDecimalScale?: boolean;
  thousandSeparator?: boolean;
  prefix?: string;
  suffix?: string;
  max?: number;
  onChange: (event: NumericInputEvent) => void;
  value?: string | number;
}

const NumericFormatCustom = React.forwardRef<HTMLInputElement, NumericFormatCustomProps>(function NumericFormatCustom(
  { onChange = noop, max, allowNegative, value, decimalScale, thousandSeparator = false, ...other },
  ref
) {
  return (
    <NumericFormat
      {...other}
      type="text"
      getInputRef={ref}
      value={value}
      allowNegative={allowNegative}
      decimalScale={decimalScale}
      valueIsNumericString={thousandSeparator}
      thousandSeparator={thousandSeparator}
      allowLeadingZeros={false}
      onValueChange={(values) => {
        const { floatValue } = values;
        if (max != null && floatValue != null && floatValue > max) {
          return;
        }
        onChange(floatValue);
      }}
    />
  );
});

interface NumericInputProps extends Omit<StandardTextFieldProps, 'onChange' | 'value'> {
  value?: string | number;
  onChange?: (event: { target: { name: string; value: string | number } }) => void;
  allowNegative?: boolean;
  max?: number;
  decimalScale?: number;
  thousandSeparator?: boolean;
  InputProps?: Omit<Partial<InputProps>, 'inputComponent' | 'inputProps'>;
}

const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  (
    {
      value,
      onChange = noop,
      allowNegative = true,
      max,
      decimalScale = 0,
      thousandSeparator = false,
      prefix,
      InputProps: { startAdornment, endAdornment, ...restInputProps } = {},
      ...props
    },
    ref
  ) => {
    return (
      <TextField
        {...props}
        value={value}
        onChange={onChange}
        sx={{ ...hideNumberInputControls, ...props.sx }}
        InputProps={{
          ...restInputProps,
          startAdornment,
          endAdornment,
          inputComponent: NumericFormatCustom as any,
          inputProps: {
            allowNegative,
            max,
            decimalScale,
            thousandSeparator,
            ref,
            value,
            prefix,
          },
        }}
      />
    );
  }
);

export default NumericInput;
